import { useState, useEffect, useCallback, useRef } from "react";
import { useAuthStore } from "../../../store/auth-store";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../../../components/ui/alert";
import { BrandButton } from "../../../components/brand/BrandButton";
import { Clock, AlertTriangle, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * SessionExpiryWarning Component Props
 */
export interface SessionExpiryWarningProps {
  /**
   * How many milliseconds before expiry to show warning
   * @default 900000 (15 minutes)
   */
  warningThreshold?: number;
  /**
   * Callback when user clicks re-authenticate button
   */
  onReAuthenticate?: () => void;
  /**
   * Position of the warning banner
   * @default 'top'
   */
  position?: "top" | "bottom";
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Whether the warning can be dismissed
   * @default true
   */
  dismissible?: true;
}

/**
 * SessionExpiryWarning Component
 *
 * Displays a warning when the user's session is about to expire.
 * Shows a countdown and provides option to re-authenticate.
 *
 * Features:
 * - Shows warning 15 minutes before expiry by default
 * - Real-time countdown display
 * - Re-authentication button
 * - Dismissible notification
 * - Non-intrusive banner design
 * - Mobile-friendly
 *
 * @example
 * ```tsx
 * <SessionExpiryWarning
 *   onReAuthenticate={handleReAuth}
 *   warningThreshold={900000} // 15 minutes
 * />
 * ```
 */
export function SessionExpiryWarning({
  warningThreshold = 900000, // 15 minutes in milliseconds
  onReAuthenticate,
  position = "top",
  className,
  dismissible = true,
}: SessionExpiryWarningProps) {
  const { isAuthenticated, expiresAt, lastVerified } = useAuthStore();
  const [showWarning, setShowWarning] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);

  /**
   * Calculate if warning should be shown
   */
  const shouldShowWarning = useCallback(() => {
    if (!isAuthenticated || !expiresAt || dismissed) {
      return false;
    }

    const now = Date.now();
    const timeUntilExpiry = expiresAt - now;

    // Session already expired
    if (timeUntilExpiry <= 0) {
      return false;
    }

    // Within warning threshold
    return timeUntilExpiry <= warningThreshold;
  }, [isAuthenticated, expiresAt, dismissed, warningThreshold]);

  /**
   * Update warning visibility and time remaining
   */
  useEffect(() => {
    const updateWarning = () => {
      const shouldShow = shouldShowWarning();
      setShowWarning(shouldShow);

      if (shouldShow && expiresAt) {
        const remaining = Math.max(0, expiresAt - Date.now());
        setTimeRemaining(remaining);
      }
    };

    // Initial check
    updateWarning();

    // Update every second
    const interval = setInterval(updateWarning, 1000);

    return () => clearInterval(interval);
  }, [shouldShowWarning, expiresAt]);

  /**
   * Track lastVerified to detect new authentication events
   * Only reset dismissed state for recent authentications (within 1 second)
   * This prevents the warning from reappearing after successful re-authentication
   */
  const lastVerifiedRef = useRef<number | null>(null);

  useEffect(() => {
    if (lastVerified && lastVerified !== lastVerifiedRef.current) {
      const isRecentAuth = Date.now() - lastVerified < 1000;
      if (isRecentAuth) {
        setDismissed(false);
      }
      lastVerifiedRef.current = lastVerified;
    }
  }, [lastVerified]);

  /**
   * Format time remaining as human-readable string
   */
  const formatTimeRemaining = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      const remainingHours = hours % 24;
      return `${days} day${days !== 1 ? "s" : ""} ${remainingHours} hour${remainingHours !== 1 ? "s" : ""}`;
    }

    if (hours > 0) {
      const remainingMinutes = minutes % 60;
      return `${hours} hour${hours !== 1 ? "s" : ""} ${remainingMinutes} min${remainingMinutes !== 1 ? "s" : ""}`;
    }

    if (minutes > 0) {
      return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
    }

    return `${seconds} second${seconds !== 1 ? "s" : ""}`;
  };

  /**
   * Handle dismiss
   */
  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  /**
   * Handle re-authenticate
   */
  const handleReAuthenticate = useCallback(() => {
    if (onReAuthenticate) {
      onReAuthenticate();
    }
  }, [onReAuthenticate]);

  // Don't render if not showing warning
  if (!showWarning) {
    return null;
  }

  return (
    <div
      className={cn(
        "fixed left-0 right-0 z-40 animate-in slide-in-from-top-2 fade-in duration-300",
        position === "top" ? "top-[80px]" : "bottom-0",
        className,
      )}
    >
      <div className="max-w-7xl mx-auto px-6 py-2">
        <Alert
          className={cn(
            "relative border-warning/50 bg-warning/10 shadow-lg",
            "flex items-center justify-between gap-4",
          )}
        >
          <div className="flex items-start gap-3 flex-1">
            <AlertTriangle
              className="h-5 w-5 text-warning flex-shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <div className="flex-1 space-y-1">
              <AlertTitle className="text-foreground text-sm font-semibold flex items-center gap-2">
                <Clock className="h-4 w-4" aria-hidden="true" />
                Session Expiring Soon
              </AlertTitle>
              <AlertDescription className="text-muted-foreground text-xs">
                Your session will expire in{" "}
                <span className="font-semibold text-warning">
                  {formatTimeRemaining(timeRemaining)}
                </span>
                . Re-authenticate to continue using the app.
              </AlertDescription>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {onReAuthenticate && (
              <BrandButton
                variant="primary"
                size="sm"
                onClick={handleReAuthenticate}
                className="flex-shrink-0"
              >
                Re-authenticate
              </BrandButton>
            )}

            {dismissible && (
              <button
                onClick={handleDismiss}
                className="p-1 rounded-md hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                aria-label="Dismiss warning"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>
        </Alert>
      </div>
    </div>
  );
}

/**
 * Compact variant for mobile or constrained spaces
 */
export interface SessionExpiryWarningCompactProps extends Omit<
  SessionExpiryWarningProps,
  "position"
> {
  /**
   * Use toast notification instead of banner
   */
  useToast?: boolean;
}

/**
 * SessionExpiryWarningCompact Component
 *
 * A more compact version of the session expiry warning,
 * suitable for mobile or constrained layouts.
 */
export function SessionExpiryWarningCompact({
  warningThreshold = 900000,
  onReAuthenticate,
  className,
  dismissible = true,
}: SessionExpiryWarningCompactProps) {
  return (
    <SessionExpiryWarning
      warningThreshold={warningThreshold}
      onReAuthenticate={onReAuthenticate}
      position="bottom"
      className={className}
      dismissible={dismissible}
    />
  );
}
